import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileText, Send, Edit, MessageSquare, CheckCircle, Clock, FileCheck, Building2, Package, MapPin, Calendar, AlertCircle, Grid3X3, Receipt } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DeadlineTypeBadge, DeadlineType } from '@/components/jobs/DeadlineTypeBadge';
import { SpecificationDataRenderer } from '@/components/specifications/SpecificationDataRenderer';
import { usePlatformSetting } from '@/hooks/usePlatformSettings';
import { useUserRole } from '@/hooks/useUserRole';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface PurchaseOrderDetailsProps {
  purchaseOrder: any;
  onSend?: () => void;
  onRequestModification?: () => void;
  onViewModifications?: () => void;
  onVendorAccept?: () => void;
  isVendor?: boolean;
}

export function PurchaseOrderDetails({ 
  purchaseOrder, 
  onSend, 
  onRequestModification,
  onViewModifications,
  onVendorAccept,
  isVendor = false,
}: PurchaseOrderDetailsProps) {
  const { isVendor: isVendorRole } = useUserRole();
  const { data: platformFeeSetting } = usePlatformSetting('platform_fee_percentage');

  const feePercentage = parseFloat(platformFeeSetting?.setting_value || '10');
  
  // Get vendor tax from PO (embedded at creation) with fallback to bid for older POs
  const vendorTaxCents = purchaseOrder.total_vendor_tax_cents || purchaseOrder.bid?.total_vendor_tax_cents || 0;
  const taxBreakdown = purchaseOrder.tax_breakdown || [];
  
  // Platform fee is calculated on base amount only (not tax)
  const platformFeeCents = Math.floor(purchaseOrder.po_amount_cents * (feePercentage / 100));
  const vendorPayoutCents = (purchaseOrder.po_amount_cents - platformFeeCents) + vendorTaxCents;

  const formatCurrency = (cents: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(cents / 100);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'draft':
        return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Draft</Badge>;
      case 'pending_acceptance':
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">Pending Acceptance</Badge>;
      case 'current':
      case 'acknowledged':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Current</Badge>;
      case 'superseded':
        return <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">Superseded</Badge>;
      default:
        return <Badge variant="secondary">{status.replace('_', ' ')}</Badge>;
    }
  };


  const lineItems = Array.isArray(purchaseOrder.line_items) ? purchaseOrder.line_items : [];
  
  // System milestone types to filter out - only show user-generated milestones (including anchor milestones)
  const SYSTEM_MILESTONE_TYPES = [
    'job_created', 'specifications_complete', 'delivery_details_complete', 
    'rfq_published', 'vendor_awarded', 'milestones_approved', 'payment_setup_complete',
    'purchase_order_created', 'in_progress', 'delivery_proof_uploaded', 
    'job_completion_submitted', 'job_accepted', 'job_closed'
  ];
  
  const milestones = (purchaseOrder.milestones || [])
    .filter((m: any) => !SYSTEM_MILESTONE_TYPES.includes(m.milestone_type))
    .sort((a: any, b: any) => 
      new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
    );
  const deliveryDetails = purchaseOrder.delivery_details;
  const job = purchaseOrder.job;
  const vendor = purchaseOrder.vendor;
  const specifications = purchaseOrder.specifications || [];
  
  // Extract shipping terms and delivery locations
  const shippingTerms = purchaseOrder.bid?.rfqs?.shipping_terms;
  const deliveryLocations = purchaseOrder.delivery_locations || [];
  
  // Find the Final Delivery Due date from milestones
  const finalDeliveryDueMilestone = milestones.find(
    (m: any) => m.milestone_name === 'Final Delivery Due'
  );
  const finalDeliveryDueDate = finalDeliveryDueMilestone?.due_date || job?.due_date;
  
  // Check if job is in a finalized state (read-only)
  const isJobFinalized = job && ['accepted', 'rejected', 'closed'].includes(job.status);

  return (
    <div className="space-y-8 max-w-7xl mx-auto p-6">
      {/* Vendor Acceptance Notice */}
      {isVendor && purchaseOrder.status === 'sent_to_vendor' && (
        <Alert className="border-primary bg-primary/5">
          <AlertCircle className="h-4 w-4 text-primary" />
          <AlertDescription className="text-sm">
            <strong>Action Required:</strong> Please review all purchase order details, line items, milestones, and terms below. Once you've reviewed everything, scroll to the bottom of the page to accept this purchase order and begin work.
          </AlertDescription>
        </Alert>
      )}

      {/* Header Section */}
      <div className="bg-card border rounded-lg p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold">{purchaseOrder.po_number}</h1>
            {job && (
              <div className="space-y-1 text-sm text-muted-foreground">
                <p>
                  <span className="font-medium">Job Reference:</span>{' '}
                  <Link to={`/jobs/${job.public_id}`} className="text-primary hover:underline">
                    {job.title} ({job.public_id})
                  </Link>
                </p>
                <p><span className="font-medium">Issue Date:</span> {formatDate(purchaseOrder.created_at)}</p>
                {(purchaseOrder.last_significant_update_at || 
                  (purchaseOrder.updated_at && purchaseOrder.updated_at !== purchaseOrder.created_at)) && (
                  <p>
                    <span className="font-medium">Last Updated:</span>{' '}
                    {formatDate(purchaseOrder.last_significant_update_at || purchaseOrder.updated_at)}
                  </p>
                )}
                {job.rfqs?.[0] && (
                  <p>
                    <span className="font-medium">RFQ Reference:</span>{' '}
                    <Link to={`/rfqs/${job.rfqs[0].public_id}`} className="text-primary hover:underline">
                      {job.rfqs[0].public_id}
                    </Link>
                  </p>
                )}
                {purchaseOrder.bid && (
                  <p><span className="font-medium">Bid Reference:</span> {purchaseOrder.bid.public_id}</p>
                )}
              </div>
            )}
          </div>
          <div className="text-right space-y-2">
            {getStatusBadge(purchaseOrder.status)}
            {purchaseOrder.version && (
              <p className="text-sm text-muted-foreground">Version {purchaseOrder.version}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 flex-wrap">
          {onSend && purchaseOrder.status === 'draft' && (
            <Button onClick={onSend}>
              <Send className="h-4 w-4 mr-2" />
              Send to Vendor
            </Button>
          )}

          {onVendorAccept && purchaseOrder.status === 'sent_to_vendor' && (
            <Button onClick={onVendorAccept}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Accept Purchase Order
            </Button>
          )}
          
          {isJobFinalized && (
            <div className="w-full">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  This job has been finalized and is now read-only. No further adjustments can be made.
                </AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      </div>

      {/* Issued By and Issued To Organizations Section - Side by Side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Issued By (Client Organization) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Issued By (Buyer)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {purchaseOrder.client_company ? (
              <>
                {/* Company Name */}
                <p className="font-semibold">{purchaseOrder.client_company.name}</p>
                
                {/* Company Address */}
                {purchaseOrder.client_company.address ? (
                  <>
                    {purchaseOrder.client_company.address.street_address && (
                      <p className="text-sm">{purchaseOrder.client_company.address.street_address}</p>
                    )}
                    {(() => {
                      const addr = purchaseOrder.client_company.address;
                      const state = addr.state || addr.state_province;
                      const zip = addr.zip_code || addr.postal_code;
                      const parts = [addr.city, state, zip].filter(Boolean);
                      return parts.length > 0 ? (
                        <p className="text-sm">{parts.join(', ')}</p>
                      ) : null;
                    })()}
                    {purchaseOrder.client_company.address.country && (
                      <p className="text-sm">{purchaseOrder.client_company.address.country}</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Address information not available</p>
                )}

                {/* Person */}
                {(purchaseOrder.client_company.person?.first_name || purchaseOrder.client_company.person?.last_name || purchaseOrder.created_by_user?.email) && (
                  <>
                    <Separator className="my-4" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-muted-foreground">Issued By:</p>
                      <p className="text-sm">
                        {purchaseOrder.client_company.person?.first_name && purchaseOrder.client_company.person?.last_name
                          ? `${purchaseOrder.client_company.person.first_name} ${purchaseOrder.client_company.person.last_name}`
                          : purchaseOrder.created_by_user?.email || 'User Name Not Set'}
                      </p>
                      {purchaseOrder.client_company.person?.job_title && (
                        <p className="text-sm text-muted-foreground">{purchaseOrder.client_company.person.job_title}</p>
                      )}
                    </div>
                  </>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Client organization information not available</p>
            )}
          </CardContent>
        </Card>

        {/* Issued To (Vendor Organization) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Issued To (Vendor)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {purchaseOrder.vendor_company ? (
              <>
                {/* Company Name */}
                <div className="flex items-center gap-2">
                  <p className="font-semibold">{purchaseOrder.vendor_company.name}</p>
                  {purchaseOrder.vendor_organization?.is_verified && (
                    <Badge variant="outline" className="text-xs">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Verified
                    </Badge>
                  )}
                </div>

                {/* Company Address */}
                {purchaseOrder.vendor_company.address?.street_address || 
                 purchaseOrder.vendor_company.address?.city || 
                 purchaseOrder.vendor_company.address?.state ? (
                  <>
                    {purchaseOrder.vendor_company.address.street_address && (
                      <p className="text-sm">{purchaseOrder.vendor_company.address.street_address}</p>
                    )}
                    {(() => {
                      const addr = purchaseOrder.vendor_company.address;
                      const state = addr.state || addr.state_province;
                      const zip = addr.zip_code || addr.postal_code;
                      const parts = [addr.city, state, zip].filter(Boolean);
                      return parts.length > 0 ? (
                        <p className="text-sm">{parts.join(', ')}</p>
                      ) : null;
                    })()}
                    {purchaseOrder.vendor_company.address.country && (
                      <p className="text-sm">{purchaseOrder.vendor_company.address.country}</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Address information not available</p>
                )}

                {/* Primary Contact */}
                {(purchaseOrder.vendor_company.person?.first_name || purchaseOrder.vendor_company.person?.last_name || purchaseOrder.vendor_primary_contact?.email) && (
                  <>
                    <Separator className="my-4" />
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-muted-foreground">Primary Contact:</p>
                      <p className="text-sm">
                        {purchaseOrder.vendor_company.person?.first_name && purchaseOrder.vendor_company.person?.last_name
                          ? `${purchaseOrder.vendor_company.person.first_name} ${purchaseOrder.vendor_company.person.last_name}`
                          : purchaseOrder.vendor_primary_contact?.email || purchaseOrder.vendor_primary_contact?.contact_email || 'Contact Name Not Set'}
                      </p>
                      {purchaseOrder.vendor_company.person?.job_title && (
                        <p className="text-sm text-muted-foreground">{purchaseOrder.vendor_company.person.job_title}</p>
                      )}
                    </div>
                  </>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Vendor information not available</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* PO Timeline */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileCheck className="h-5 w-5" />
              PO Timeline
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {purchaseOrder.sent_to_vendor_at && (
              <div className="grid grid-cols-2 gap-2">
                <span className="text-muted-foreground">Sent to Vendor:</span>
                <span className="font-medium">{formatDate(purchaseOrder.sent_to_vendor_at)}</span>
              </div>
            )}
            {purchaseOrder.vendor_acknowledged_at && (
              <div className="grid grid-cols-2 gap-2">
                <span className="text-muted-foreground">Vendor Accepted:</span>
                <span className="font-medium">{formatDate(purchaseOrder.vendor_acknowledged_at)}</span>
              </div>
            )}
            {/* Applied Change Orders */}
            {lineItems
              .filter((item: any) => item.adjustment === true && item.item_number && item.adjustment_date)
              .sort((a: any, b: any) => new Date(a.adjustment_date).getTime() - new Date(b.adjustment_date).getTime())
              .map((co: any) => (
                <div key={co.item_number} className="grid grid-cols-2 gap-2">
                  <span className="text-muted-foreground">Item #{co.item_number} Applied:</span>
                  <span className="font-medium">{formatDate(co.adjustment_date)}</span>
                </div>
              ))}
          </CardContent>
        </Card>

        {/* Financial Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Financial Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal:</span>
              <span className="font-medium">{formatCurrency(purchaseOrder.po_amount_cents, purchaseOrder.currency)}</span>
            </div>
            
            {/* Vendor-Only Platform Fee Breakdown */}
            {isVendorRole && (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Platform Fee ({feePercentage}%):</span>
                  <span className="font-medium">-{formatCurrency(platformFeeCents, purchaseOrder.currency)}</span>
                </div>
                {vendorTaxCents > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Sales Tax (pass-through):</span>
                    <span className="font-medium">+{formatCurrency(vendorTaxCents, purchaseOrder.currency)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between text-lg font-bold">
                  <span>Your Payout:</span>
                  <span>{formatCurrency(vendorPayoutCents, purchaseOrder.currency)}</span>
                </div>
              </>
            )}
            
            {/* Client View - Show tax if applicable */}
            {!isVendorRole && (
              <>
                {vendorTaxCents > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Estimated Tax:</span>
                    <span className="font-medium">{formatCurrency(vendorTaxCents, purchaseOrder.currency)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total Amount:</span>
                  <span>{formatCurrency(purchaseOrder.po_amount_cents + vendorTaxCents, purchaseOrder.currency)}</span>
                </div>
              </>
            )}
            
            {purchaseOrder.payment_terms && (
              <p className="text-xs text-muted-foreground">Payment Terms: {purchaseOrder.payment_terms}</p>
            )}
            <p className="text-xs text-muted-foreground">Subject to the Purchase Order Terms and Conditions incorporated by reference below.</p>
          </CardContent>
        </Card>
      </div>

      {/* Tax Information Section */}
      {vendorTaxCents > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Tax Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between text-lg font-semibold">
              <span>Total Estimated Sales Tax:</span>
              <span>{formatCurrency(vendorTaxCents, purchaseOrder.currency)}</span>
            </div>
            
            {taxBreakdown.length > 0 && (
              <>
                <Separator />
                <div>
                  <p className="text-sm font-medium mb-2">Tax Breakdown by Destination:</p>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Item</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Unit Cost</TableHead>
                          <TableHead>Destination</TableHead>
                          <TableHead className="text-right">Taxable Amt</TableHead>
                          <TableHead className="text-right">Rate</TableHead>
                          <TableHead className="text-right">Tax</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {taxBreakdown.map((row: any, idx: number) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{row.item_description}</TableCell>
                            <TableCell className="text-right">{row.quantity?.toLocaleString() || '-'}</TableCell>
                            <TableCell className="text-right">
                              {row.unit_price_cents ? formatCurrency(row.unit_price_cents, purchaseOrder.currency) : '-'}
                            </TableCell>
                            <TableCell>
                              {row.destination_name || row.destination_city ? (
                                <div className="space-y-0.5">
                                  {row.destination_name && <span className="font-medium block">{row.destination_name}</span>}
                                  <span className="text-muted-foreground text-xs">
                                    {row.destination_city}, {row.destination_state} {row.destination_zip}
                                  </span>
                                </div>
                              ) : (
                                row.destination_zip || 'N/A'
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {row.taxable_amount_cents ? formatCurrency(row.taxable_amount_cents, purchaseOrder.currency) : '-'}
                            </TableCell>
                            <TableCell className="text-right">{row.tax_rate_percentage != null ? `${row.tax_rate_percentage}%` : 'N/A'}</TableCell>
                            <TableCell className="text-right">
                              {formatCurrency(row.vendor_tax_cents || row.calculated_tax_cents || 0, purchaseOrder.currency)}
                              {row.is_manually_edited && (
                                <span className="ml-1 text-xs text-muted-foreground">(edited)</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
            )}
            
            <p className="text-xs text-muted-foreground">
              Tax estimates provided by vendor at time of bid submission. Final tax may vary based on actual delivery locations and applicable tax rules.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Line Items Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Line Items
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {lineItems.length > 0 ? (
              lineItems.map((item: any, index: number) => (
                <div key={index} className="border rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold">Item #{item.item_number || index + 1}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">
                        {formatCurrency(item.total_cents || 0, purchaseOrder.currency)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Qty: {item.quantity || 1} × {formatCurrency(item.unit_price_cents || 0, purchaseOrder.currency)}
                      </p>
                      {item.tax_cents > 0 && (
                        <p className="text-sm text-muted-foreground">
                          Tax: {formatCurrency(item.tax_cents, purchaseOrder.currency)}
                        </p>
                      )}
                    </div>
                  </div>
                  {item.specifications && Object.keys(item.specifications).length > 0 && (
                    <div className="border-t pt-3 mt-3">
                      <p className="text-sm font-medium mb-2">Specifications:</p>
                      <div className="text-sm">
                        <SpecificationDataRenderer content={item.specifications} />
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-muted-foreground text-center py-4">No line items available</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delivery Information Section */}
      {deliveryDetails && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Delivery Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="font-semibold mb-2">Delivery Address:</p>
              <p>{deliveryDetails.address_line1}</p>
              {deliveryDetails.address_line2 && <p>{deliveryDetails.address_line2}</p>}
              <p>{deliveryDetails.city}, {deliveryDetails.state_province} {deliveryDetails.postal_code}</p>
              <p>{deliveryDetails.country}</p>
            </div>
            
            {deliveryDetails.contact_name && (
              <div>
                <p className="font-semibold mb-2">Delivery Contact:</p>
                <p className="text-sm">Name: {deliveryDetails.contact_name}</p>
                {deliveryDetails.contact_phone && <p className="text-sm">Phone: {deliveryDetails.contact_phone}</p>}
                {deliveryDetails.contact_email && <p className="text-sm">Email: {deliveryDetails.contact_email}</p>}
              </div>
            )}

            {deliveryDetails.preferred_delivery_date && (
              <div className="text-sm flex items-center gap-2">
                <span className="font-semibold">Requested Delivery Date:</span> 
                <span>{formatDateOnly(deliveryDetails.preferred_delivery_date)}</span>
                {deliveryDetails.deadline_type && (
                  <DeadlineTypeBadge type={deliveryDetails.deadline_type as DeadlineType} />
                )}
              </div>
            )}

            {deliveryDetails.special_instructions && (
              <div>
                <p className="font-semibold mb-2">Special Delivery Instructions:</p>
                <p className="text-sm text-muted-foreground">{deliveryDetails.special_instructions}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}


      {/* Shipping Terms and Delivery Information Section */}
      {(shippingTerms || deliveryLocations.length > 0 || finalDeliveryDueDate) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Shipping Terms and Delivery Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Final Delivery Due Date */}
            {finalDeliveryDueDate && (
              <div className="border rounded-lg p-4 bg-primary/5 border-primary/20">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-primary" />
                  Delivery Due Date
                </h4>
                <p className="text-lg font-bold">
                  {formatDateOnly(finalDeliveryDueDate)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  All deliverables must be completed by this date
                </p>
              </div>
            )}

            {/* Shipping Terms */}
            {shippingTerms && (
              <div className="border rounded-lg p-4 bg-muted/30">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Shipping Terms
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-sm">
                      {shippingTerms === 'fob_origin' ? 'FOB Origin' : 'FOB Destination'}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {shippingTerms === 'fob_origin' 
                      ? "Buyer assumes risk and pays shipping costs from vendor's location. Title transfers to buyer when goods are shipped."
                      : "Vendor assumes risk and pays shipping costs to delivery location. Title transfers to buyer upon delivery."}
                  </p>
                </div>
              </div>
            )}

            {/* Delivery Locations */}
            {deliveryLocations.length > 0 && (
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Delivery Locations ({deliveryLocations.length})
                </h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {deliveryLocations.map((delivery: any, index: number) => (
                    <div key={delivery.id || index} className="border rounded-lg p-4 space-y-3 h-full">
                      {/* Location Header */}
                      <div className="flex items-start justify-between">
                        <div>
                          <h5 className="font-semibold">{delivery.delivery_name}</h5>
                          {delivery.location_type && (
                            <Badge variant="outline" className="mt-1 text-xs">
                              {delivery.location_type}
                            </Badge>
                          )}
                        </div>
                        {delivery.delivery_order && (
                          <Badge variant="secondary">Stop #{delivery.delivery_order}</Badge>
                        )}
                      </div>

                      {/* Address */}
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Address:</p>
                        <div className="text-sm">
                          <p>{delivery.address_line1}</p>
                          {delivery.address_line2 && <p>{delivery.address_line2}</p>}
                          <p>{delivery.city}, {delivery.state_province} {delivery.postal_code}</p>
                          <p>{delivery.country}</p>
                        </div>
                      </div>

                      {/* Contact Information */}
                      {(delivery.contact_name || delivery.contact_phone || delivery.contact_email) && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-1">Contact:</p>
                          <div className="text-sm space-y-1">
                            {delivery.contact_name && <p>{delivery.contact_name}</p>}
                            {delivery.contact_phone && <p>Phone: {delivery.contact_phone}</p>}
                            {delivery.contact_email && <p>Email: {delivery.contact_email}</p>}
                          </div>
                        </div>
                      )}

                      {/* Delivery Date */}
                      {delivery.preferred_delivery_date && (
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">Requested Delivery:</span>
                          <span>{formatDateOnly(delivery.preferred_delivery_date)}</span>
                          {delivery.deadline_type && (
                            <DeadlineTypeBadge type={delivery.deadline_type as DeadlineType} />
                          )}
                        </div>
                      )}

                      {/* Special Instructions */}
                      {delivery.special_instructions && (
                        <div className="border-t pt-3">
                          <p className="text-sm font-medium text-muted-foreground mb-1">Special Instructions:</p>
                          <p className="text-sm">{delivery.special_instructions}</p>
                        </div>
                      )}

                      {/* Access Requirements */}
                      {delivery.access_requirements && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-1">Access Requirements:</p>
                          <p className="text-sm">{delivery.access_requirements}</p>
                        </div>
                      )}

                      {/* Delivery Requirements */}
                      {(delivery.signature_required || delivery.id_required || delivery.appointment_required) && (
                        <div className="flex flex-wrap gap-2">
                          {delivery.signature_required && (
                            <Badge variant="outline" className="text-xs">Signature Required</Badge>
                          )}
                          {delivery.id_required && (
                            <Badge variant="outline" className="text-xs">ID Required</Badge>
                          )}
                          {delivery.appointment_required && (
                            <Badge variant="outline" className="text-xs">Appointment Required</Badge>
                          )}
                        </div>
                      )}

                      {/* Dock Information */}
                      {delivery.dock_information && (
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-1">Dock Information:</p>
                          <p className="text-sm">{delivery.dock_information}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity Distribution by Location */}
            {purchaseOrder.quantity_distribution && 
             purchaseOrder.quantity_distribution.length > 0 && 
             deliveryLocations.length > 1 && (
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Grid3X3 className="h-4 w-4" />
                  Quantity Distribution by Location
                </h4>
                {(() => {
                  // Check if any quantities are finalized
                  const hasFinalized = purchaseOrder.quantity_distribution.some(
                    (dist: any) => dist.is_finalized && dist.quantity > 0
                  );
                  const hasQuantities = purchaseOrder.quantity_distribution.some(
                    (dist: any) => dist.quantity > 0
                  );
                  const showPercentages = !hasQuantities;

                  return (
                    <>
                      <div className="flex items-center gap-2 mb-4">
                        <p className="text-sm text-muted-foreground">
                          {showPercentages 
                            ? 'Percentage allocation for each location' 
                            : 'Quantities to be delivered to each location'}
                        </p>
                        {showPercentages && (
                          <Badge variant="outline" className="text-xs">Based on % allocation</Badge>
                        )}
                      </div>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="min-w-[200px]">Specification</TableHead>
                              {deliveryLocations.map((loc: any) => (
                                <TableHead key={loc.id} className="text-right min-w-[120px]">
                                  {loc.delivery_name}
                                </TableHead>
                              ))}
                              <TableHead className="text-right min-w-[100px]">Total</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(() => {
                              // Build a lookup of total quantities per specification from line items
                              const specQuantityLookup: Record<string, number> = {};
                              lineItems.forEach((item: any) => {
                                if (item.specification_id && item.quantity) {
                                  specQuantityLookup[item.specification_id] = item.quantity;
                                }
                              });

                              // Group distribution by specification
                              const specDistributions: Record<string, { 
                                specName: string; 
                                unit: string;
                                locations: Record<string, { quantity: number; percentage: number | null }>;
                                total: number;
                              }> = {};

                              // Filter to only show awarded specifications
                              const awardedSpecIds = new Set(lineItems.map((li: any) => li.specification_id));
                              const awardedDistribution = purchaseOrder.quantity_distribution.filter(
                                (dist: any) => awardedSpecIds.has(dist.specification_id)
                              );

                              awardedDistribution.forEach((dist: any) => {
                                const specId = dist.specification_id;
                                if (!specDistributions[specId]) {
                                  const lineItem = lineItems.find((li: any) => li.specification_id === specId);
                                  specDistributions[specId] = {
                                    specName: lineItem?.description || 'Unknown Specification',
                                    unit: dist.unit || 'units',
                                    locations: {},
                                    total: 0
                                  };
                                }
                                specDistributions[specId].locations[dist.delivery_location_id] = {
                                  quantity: dist.quantity || 0,
                                  percentage: dist.percentage
                                };
                                specDistributions[specId].total += dist.quantity || 0;
                              });

                              return Object.entries(specDistributions).map(([specId, data]) => {
                                const totalQty = specQuantityLookup[specId] || data.total;
                                
                                return (
                                  <TableRow key={specId}>
                                    <TableCell className="font-medium">{data.specName}</TableCell>
                                    {deliveryLocations.map((loc: any) => {
                                      const locData = data.locations[loc.id];
                                      if (showPercentages) {
                                        const calculatedQty = totalQty > 0 && locData?.percentage != null
                                          ? Math.round((locData.percentage / 100) * totalQty)
                                          : 0;
                                        return (
                                          <TableCell key={loc.id} className="text-right">
                                            {locData?.percentage != null ? (
                                              <span>
                                                {locData.percentage}%
                                                {calculatedQty > 0 && (
                                                  <span className="text-muted-foreground ml-1">
                                                    ({calculatedQty.toLocaleString()})
                                                  </span>
                                                )}
                                              </span>
                                            ) : '—'}
                                          </TableCell>
                                        );
                                      }
                                      return (
                                        <TableCell key={loc.id} className="text-right">
                                          {(locData?.quantity || 0).toLocaleString()} {data.unit}
                                        </TableCell>
                                      );
                                    })}
                                    <TableCell className="text-right font-semibold">
                                      {totalQty.toLocaleString()} {data.unit}
                                    </TableCell>
                                  </TableRow>
                                );
                              });
                            })()}
                          </TableBody>
                        </Table>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Terms and Conditions Section */}
      <Card>
        <CardHeader>
          <CardTitle>Terms and Conditions</CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <p className="text-muted-foreground">
            This Purchase Order is governed by the Purchase Order Terms and Conditions 
            available at{' '}
            <a 
              href="https://useradius.io/po-terms" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline font-medium"
            >
              https://useradius.io/po-terms
            </a>
            , which are incorporated herein by reference.
          </p>
        </CardContent>
      </Card>

      {/* Reference Information Section */}
      <Card>
        <CardHeader>
          <CardTitle>Reference Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            {job && (
              <Link to={`/jobs/${job.public_id}`} className="flex items-center gap-2 text-primary hover:underline">
                <FileText className="h-4 w-4" />
                Original Job Posting
              </Link>
            )}
            {job?.rfqs?.[0] && (
              <Link to={`/rfqs/${job.rfqs[0].public_id}`} className="flex items-center gap-2 text-primary hover:underline">
                <FileText className="h-4 w-4" />
                Request for Quote (RFQ)
              </Link>
            )}
            {specifications.length > 0 && (
              <Link to={`/jobs/${job?.public_id}/specifications`} className="flex items-center gap-2 text-primary hover:underline">
                <FileText className="h-4 w-4" />
                Job Specifications
              </Link>
            )}
            {deliveryDetails && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-4 w-4" />
                Delivery Requirements (see above)
              </div>
            )}
            {milestones.length > 0 && (
              <Link to={`/jobs/${job?.public_id}/milestones`} className="flex items-center gap-2 text-primary hover:underline">
                <Calendar className="h-4 w-4" />
                Milestone Timeline
              </Link>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Vendor Acceptance Section */}
      <Card className="border-2">
        <CardHeader>
          <CardTitle>Vendor Acceptance</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            {purchaseOrder.vendor_acknowledged_at ? (
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-semibold">Accepted</span>
                </div>
                <p>
                  <span className="font-medium">Accepted By:</span>{' '}
                  {purchaseOrder.vendor_company?.person?.first_name && purchaseOrder.vendor_company?.person?.last_name
                    ? `${purchaseOrder.vendor_company.person.first_name} ${purchaseOrder.vendor_company.person.last_name}`
                    : purchaseOrder.vendor_primary_contact?.email || 'Unknown User'}
                </p>
                {purchaseOrder.vendor_company?.person?.job_title && (
                  <p>
                    <span className="font-medium">Title:</span> {purchaseOrder.vendor_company.person.job_title}
                  </p>
                )}
                <p>
                  <span className="font-medium">Company:</span>{' '}
                  {purchaseOrder.vendor_company?.name || vendor?.organizations?.name || 'N/A'}
                </p>
                <p>
                  <span className="font-medium">Date Accepted:</span> {formatDate(purchaseOrder.vendor_acknowledged_at)}
                </p>
                <p className="text-muted-foreground italic">Electronically signed</p>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400">
                  <Clock className="h-5 w-5" />
                  <span className="font-semibold">Pending Acceptance</span>
                </div>
                <p className="text-muted-foreground">
                  This purchase order requires vendor acceptance before work can begin.
                </p>
                {onVendorAccept && (
                  <Button onClick={onVendorAccept} className="mt-3">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Accept Purchase Order
                  </Button>
                )}
              </div>
            )}

          </div>
          
          <p className="text-xs text-muted-foreground italic">
            By accepting this Purchase Order, vendor agrees to all terms, conditions, specifications, 
            delivery requirements, and milestone schedule outlined in this document.
          </p>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="border-t pt-6 pb-4 text-center text-sm text-muted-foreground">
        <p>This is a legally binding purchase agreement.</p>
        <p className="mt-1">PO Number: {purchaseOrder.po_number}</p>
      </div>
    </div>
  );
}